import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useBlocker, useNavigate } from 'react-router';
import {
  VISITOR_LANGUAGES,
  VISITOR_TEXTS,
  type ContactFormField,
  type ContactValues,
  type VisitorLanguage,
} from '@wr/shared';
import { kioskPin, privacy } from '../../../../../config/portal.json';
import { Checkbox, Field, ManyOfChips, OneOfChips, SelectField } from '../../components/ui.js';
import { LISTS, validateContact } from '../../lists.js';
import { SCREENS } from '../../modules.js';
import { useOffline } from '../../offline/context.js';
import { newId } from '../../offline/ids.js';
import { useSignedIn } from '../../session.js';
import { PinPad } from './PinPad.js';

type Screen = 'form' | 'thanks' | 'pin';

interface Draft {
  first_name: string;
  last_name: string;
  company: string;
  email: string;
  job_title: string;
  mobile: string;
  office_phone: string;
  linkedin_url: string;
  country: string;
  interest: string;
  role: string;
  applications: string[];
  pain: string;
}

const EMPTY: Draft = {
  first_name: '',
  last_name: '',
  company: '',
  email: '',
  job_title: '',
  mobile: '',
  office_phone: '',
  linkedin_url: '',
  country: '',
  interest: '',
  role: '',
  applications: [],
  pain: '',
};

/**
 * Where the seller lands after the PIN: Is there a project? over the
 * visitor's contact (decision of Nov 2, 2026), with block 2 on Yes.
 */
export const contactPath = (id: string) => `/people/contacts/${id}/project`;

/**
 * Hand the tablet (brief section 6.4): the visitor types their own contact,
 * in their language, as docs/prototype.html draws it (.kiosk). The same
 * fields as the contact form, More info folded; Send me information after
 * the show; the consent, required, with the version of its sentence. Always
 * a contact, never a lead. Saved on the device first, as every capture.
 * Nothing leaves this screen without the kiosk PIN (the lock, Back): then
 * the seller lands on the visitor's contact.
 */
export function HandTheTablet() {
  const { user } = useSignedIn();
  const { save, currentEventId } = useOffline();
  const navigate = useNavigate();
  const [lang, setLang] = useState<VisitorLanguage>('en');
  const [screen, setScreen] = useState<Screen>('form');
  const [from, setFrom] = useState<Screen>('form');
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [sendMe, setSendMe] = useState(false);
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<ContactFormField | 'consent', string>>>({});
  const [summary, setSummary] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const savedId = useRef<string | null>(null);
  const leaving = useRef<string | null>(null);
  const T = VISITOR_TEXTS[lang];

  // The visitor never leaves by Back or a link: the PIN first.
  const blocker = useBlocker(() => leaving.current === null);
  const blocked = blocker.state === 'blocked';
  useEffect(() => {
    const onLeave = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', onLeave);
    document.body.classList.add('kiosk-mode');
    return () => {
      window.removeEventListener('beforeunload', onLeave);
      document.body.classList.remove('kiosk-mode');
    };
  }, []);

  // After the PIN: the visitor's contact when one was saved, else the home page.
  const unlock = useCallback(() => {
    // People opens the contact once it exists (Capture part 4); until then, the home page.
    const to = savedId.current && SCREENS.people ? contactPath(savedId.current) : '/';
    if (blocker.state === 'blocked') blocker.reset();
    leaving.current = to;
    navigate(to, { replace: true });
  }, [navigate, blocker]);

  const set = <K extends keyof Draft>(field: K, value: Draft[K]) => {
    setDraft((d) => ({ ...d, [field]: value }));
    setErrors({});
    setSummary(null);
  };

  async function send(e: FormEvent) {
    e.preventDefault();
    if (saving) return;
    const result = validateContact(draft);
    const next: typeof errors = {};
    if (!result.ok) {
      for (const error of result.errors) {
        next[error.field] =
          error.code === 'required' ? T.required : error.field === 'email' ? T.badEmail : T.invalid;
      }
    }
    if (!consent) next.consent = T.consentNeeded;
    if (Object.keys(next).length > 0) {
      setErrors(next);
      const missing = result.ok ? [] : result.errors.filter((x) => x.code === 'required');
      setSummary(missing.length > 0 ? T.required : (next.email ?? next.consent ?? T.required));
      const optional = Object.keys(next).some(
        (f) => !['first_name', 'last_name', 'company', 'email', 'consent'].includes(f),
      );
      if (optional) setMoreOpen(true);
      return;
    }
    setSaving(true);
    try {
      const id = newId();
      const values = (result as { ok: true; value: ContactValues }).value;
      await save({
        opId: newId(),
        entity: 'contact',
        id,
        action: 'upsert',
        changedAt: new Date().toISOString(),
        fields: {
          ...given(values),
          wants_content: sendMe,
          source: 'kiosk_form',
          event_id: await currentEventId(),
          consent_at: new Date().toISOString(),
          consent_text_version: privacy.consentVersion,
        },
      });
      savedId.current = id;
      setScreen('thanks');
    } catch {
      setSummary('This device could not save. Please hand the tablet back.');
    } finally {
      setSaving(false);
    }
  }

  if (screen === 'pin' || blocked) {
    return (
      <div className="kiosk">
        <div className="k-top">
          <img src="/logo.png" alt="Waste Robotics" />
          <button type="button" className="k-lock" aria-label="Exit kiosk mode" disabled>
            🔒
          </button>
        </div>
        <PinPad
          digits={kioskPin.digits}
          mode="handoff"
          onUnlock={unlock}
          onBack={() => (blocked ? blocker.reset() : setScreen(from))}
        />
      </div>
    );
  }

  if (screen === 'thanks') {
    return (
      <div className="kiosk k-attract">
        <div className="k-home">
          <span className="okdot" style={{ width: 64, height: 64 }} />
          <h1>{T.thanks}</h1>
          <p>
            {T.handBack} {user.firstName}.
          </p>
          <button
            type="button"
            className="btn huge"
            onClick={() => {
              setFrom('thanks');
              setScreen('pin');
            }}
          >
            I am the Waste Robotics rep
          </button>
        </div>
      </div>
    );
  }

  const top = (
    <div className="k-top">
      <img src="/logo.png" alt="Waste Robotics" />
      <span className="k-hint">Hand the tablet to the visitor</span>
      <button
        type="button"
        className="k-lock"
        aria-label="Exit kiosk mode"
        onClick={() => {
          setFrom('form');
          setScreen('pin');
        }}
      >
        🔒
      </button>
    </div>
  );

  const markets = LISTS.markets.map((m, i) => ({
    value: m.code,
    label: `${m.code} ${T.markets[i] ?? m.name}`,
  }));

  return (
    <div className="kiosk">
      {top}
      <form className="k-body k-form" noValidate onSubmit={(e) => void send(e)} lang={lang}>
        <div className="k-langs" role="group" aria-label="Language">
          {VISITOR_LANGUAGES.map((l) => (
            <button
              key={l}
              type="button"
              lang={l}
              aria-pressed={l === lang}
              onClick={() => {
                setLang(l);
                setErrors({});
                setSummary(null);
              }}
            >
              {VISITOR_TEXTS[l].language}
            </button>
          ))}
        </div>
        <h2>{T.title}</h2>
        <p className="note">{T.sub}</p>
        <div className="g2">
          <Field
            label={T.firstName}
            mark
            autoComplete="off"
            value={draft.first_name}
            error={errors.first_name}
            onChange={(e) => set('first_name', e.target.value)}
          />
          <Field
            label={T.lastName}
            mark
            autoComplete="off"
            value={draft.last_name}
            error={errors.last_name}
            onChange={(e) => set('last_name', e.target.value)}
          />
        </div>
        <Field
          label={T.company}
          mark
          autoComplete="off"
          value={draft.company}
          error={errors.company}
          onChange={(e) => set('company', e.target.value)}
        />
        <Field
          label={T.email}
          mark
          type="email"
          inputMode="email"
          autoComplete="off"
          value={draft.email}
          error={errors.email}
          onChange={(e) => set('email', e.target.value)}
        />
        <details
          className="k-more"
          open={moreOpen}
          onToggle={(e) => setMoreOpen((e.currentTarget as HTMLDetailsElement).open)}
        >
          <summary>{T.more}</summary>
          <div className="k-more-body">
            <Field
              label={T.jobTitle}
              value={draft.job_title}
              error={errors.job_title}
              onChange={(e) => set('job_title', e.target.value)}
            />
            <Field
              label={T.mobile}
              type="tel"
              inputMode="tel"
              value={draft.mobile}
              error={errors.mobile}
              onChange={(e) => set('mobile', e.target.value)}
            />
            <Field
              label={T.officePhone}
              type="tel"
              inputMode="tel"
              value={draft.office_phone}
              error={errors.office_phone}
              onChange={(e) => set('office_phone', e.target.value)}
            />
            <Field
              label={T.linkedin}
              type="url"
              inputMode="url"
              value={draft.linkedin_url}
              error={errors.linkedin_url}
              onChange={(e) => set('linkedin_url', e.target.value)}
            />
            <SelectField
              label={T.country}
              value={draft.country}
              error={errors.country}
              onChange={(e) => set('country', e.target.value)}
            >
              <option value="">{T.countryPick}</option>
              {LISTS.countries.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </SelectField>
            <OneOfChips
              label={T.interest}
              options={LISTS.interestOptions.map((o, i) => ({
                value: o.value,
                label: T.interests[i] ?? o.label,
              }))}
              value={draft.interest}
              onChange={(v) => set('interest', v)}
            />
            <OneOfChips
              label={T.role}
              options={LISTS.roleOptions.map((o, i) => ({
                value: o.value,
                label: T.roles[i] ?? o.label,
              }))}
              value={draft.role}
              onChange={(v) => set('role', v)}
            />
            <ManyOfChips
              label={T.applications}
              options={markets}
              value={draft.applications}
              onChange={(v) => set('applications', v)}
            />
            <Field
              label={T.pain}
              value={draft.pain}
              error={errors.pain}
              onChange={(e) => set('pain', e.target.value)}
            />
          </div>
        </details>
        <Checkbox checked={sendMe} onChange={(e) => setSendMe(e.target.checked)}>
          {T.sendMe}
        </Checkbox>
        <Checkbox
          checked={consent}
          aria-invalid={errors.consent ? true : undefined}
          onChange={(e) => {
            setConsent(e.target.checked);
            setErrors({});
            setSummary(null);
          }}
        >
          {T.consent}{' '}
          <span className="req" aria-hidden="true">
            *
          </span>
          {privacy.policyUrl && (
            <>
              {' '}
              <a href={privacy.policyUrl} target="_blank" rel="noreferrer">
                {T.privacy}
              </a>
            </>
          )}
        </Checkbox>
        <p className="k-err" role="alert">
          {summary}
        </p>
        <button type="submit" className="btn primary huge" disabled={saving}>
          {T.send}
        </button>
      </form>
    </div>
  );
}

/** Not given: an empty optional field is not sent at all. */
function given(values: ContactValues): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(values).filter(
      ([, v]) => v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0),
    ),
  );
}
